import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Button, Card, Input, Spinner } from '@realvisor/ui';
import { api } from '@/lib/api';
import { useCreateCampaign, type CreateCampaignDto } from '@/hooks/useCampaigns';

interface OutcomeRow {
  name: string;
  code: string;
  rewardAmount: number | '';
  createsLead: boolean;
  removesFromQueue: boolean;
}

const OUTCOME_CODES = [
  { value: 'interested', label: 'interested' },
  { value: 'meeting_set', label: 'meeting_set' },
  { value: 'meeting_done', label: 'meeting_done' },
  { value: 'not_interested', label: 'not_interested' },
  { value: 'unavailable', label: 'unavailable' },
  { value: 'callback', label: 'callback' },
  { value: 'wrong_number', label: 'wrong_number' },
];

const DEFAULT_OUTCOMES: OutcomeRow[] = [
  { name: 'Zájem', code: 'interested', rewardAmount: 100, createsLead: true, removesFromQueue: true },
  { name: 'Schůzka domluvena', code: 'meeting_set', rewardAmount: 150, createsLead: true, removesFromQueue: true },
  { name: 'Schůzka proběhla', code: 'meeting_done', rewardAmount: 200, createsLead: false, removesFromQueue: true },
  { name: 'Nezájem', code: 'not_interested', rewardAmount: '', createsLead: false, removesFromQueue: true },
  { name: 'Nedostupný', code: 'unavailable', rewardAmount: '', createsLead: false, removesFromQueue: false },
  { name: 'Zavolat zpět', code: 'callback', rewardAmount: '', createsLead: false, removesFromQueue: false },
  { name: 'Chybné číslo', code: 'wrong_number', rewardAmount: '', createsLead: false, removesFromQueue: true },
];

export default function CampaignCreate() {
  const navigate = useNavigate();
  const createCampaign = useCreateCampaign();

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>('/projects'),
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState('');
  const [batchSize, setBatchSize] = useState(25);
  const [outcomes, setOutcomes] = useState<OutcomeRow[]>(DEFAULT_OUTCOMES);

  const updateOutcome = (index: number, field: keyof OutcomeRow, value: any) => {
    setOutcomes((prev) => prev.map((o, i) => (i === index ? { ...o, [field]: value } : o)));
  };

  const addOutcome = () => {
    setOutcomes((prev) => [
      ...prev,
      { name: '', code: 'interested', rewardAmount: '', createsLead: false, removesFromQueue: false },
    ]);
  };

  const removeOutcome = (index: number) => {
    setOutcomes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Vyplňte název kampaně');
      return;
    }
    if (!projectId) {
      toast.error('Vyberte projekt');
      return;
    }

    const dto: CreateCampaignDto = {
      name: name.trim(),
      description: description.trim() || undefined,
      projectId,
      batchSize,
      outcomes: outcomes
        .filter((o) => o.name.trim() && o.code)
        .map((o) => ({
          name: o.name.trim(),
          code: o.code,
          rewardAmount: o.rewardAmount !== '' ? Number(o.rewardAmount) : undefined,
          createsLead: o.createsLead,
          removesFromQueue: o.removesFromQueue,
        })),
    };

    try {
      const result = await createCampaign.mutateAsync(dto);
      toast.success('Kampaň vytvořena');
      navigate(`/callcenter/campaigns/${result.id}`);
    } catch (error: any) {
      toast.error(error.message || 'Nepodařilo se vytvořit kampaň');
    }
  };

  // Resolve projects array from response (handle both array and { data: [...] })
  const projects = Array.isArray(projectsData) ? projectsData : projectsData?.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/callcenter/campaigns')}>
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nová kampaň</h1>
          <p className="text-sm text-gray-500">Vytvořte novou call centrum kampaň</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Základní info */}
        <Card padding="md">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Základní info</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Název kampaně *</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Název kampaně"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Popis</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Volitelný popis kampaně"
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Projekt *</label>
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Vyberte projekt</option>
                {projects?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Velikost balíku</label>
              <Input
                type="number"
                value={batchSize}
                onChange={(e) => setBatchSize(Number(e.target.value))}
                min={1}
              />
              <p className="mt-1 text-xs text-gray-500">Počet kontaktů přidělených operátorovi najednou</p>
            </div>
          </div>
        </Card>

        {/* Výsledky hovorů */}
        <Card padding="md" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Výsledky hovorů</h2>
            <Button type="button" variant="outline" size="sm" onClick={addOutcome}>
              <PlusIcon className="h-4 w-4 mr-1" />
              Přidat výsledek
            </Button>
          </div>

          {outcomes.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Žádné výsledky. Přidejte alespoň jeden.</p>
          ) : (
            <div className="space-y-3">
              {/* Header */}
              <div className="hidden sm:grid sm:grid-cols-12 gap-2 text-xs font-medium text-gray-500 uppercase tracking-wider px-1">
                <div className="col-span-3">Název</div>
                <div className="col-span-2">Kód</div>
                <div className="col-span-2">Odměna Kč</div>
                <div className="col-span-2 text-center">Vytvoří lead</div>
                <div className="col-span-2 text-center">Vyřadí z fronty</div>
                <div className="col-span-1" />
              </div>

              {outcomes.map((outcome, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center p-2 rounded-lg bg-gray-50"
                >
                  <div className="sm:col-span-3">
                    <Input
                      value={outcome.name}
                      onChange={(e) => updateOutcome(index, 'name', e.target.value)}
                      placeholder="Název"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <select
                      value={outcome.code}
                      onChange={(e) => updateOutcome(index, 'code', e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
                    >
                      {OUTCOME_CODES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <Input
                      type="number"
                      value={outcome.rewardAmount}
                      onChange={(e) =>
                        updateOutcome(index, 'rewardAmount', e.target.value === '' ? '' : Number(e.target.value))
                      }
                      placeholder="0"
                      min={0}
                    />
                  </div>
                  <div className="sm:col-span-2 flex items-center justify-center">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={outcome.createsLead}
                        onChange={(e) => updateOutcome(index, 'createsLead', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="sm:hidden">Vytvoří lead</span>
                    </label>
                  </div>
                  <div className="sm:col-span-2 flex items-center justify-center">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={outcome.removesFromQueue}
                        onChange={(e) => updateOutcome(index, 'removesFromQueue', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="sm:hidden">Vyřadí z fronty</span>
                    </label>
                  </div>
                  <div className="sm:col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeOutcome(index)}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="outline" onClick={() => navigate('/callcenter/campaigns')}>
            Zpět
          </Button>
          <Button type="submit" disabled={createCampaign.isPending}>
            {createCampaign.isPending ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Vytvářím...
              </>
            ) : (
              'Vytvořit kampaň'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
